import React, { ReactElement, useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { useHistory } from "react-router";

import PublicationPlaceholder from "src/images/publication-placeholder-grey-sm.svg";
import classNames from "classnames";
import { TooltipedEllipsis } from "src/app/components/TooltipedEllipsis/TooltipedEllipsis";
import RatingSelector from "src/app/components/RatingSelector/RatingSelector";
import { RootState } from "src/redux/reducers";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { TaskType, DateObject } from "src/types";
import { ACTION_TYPES } from "../../utils";

import { ReactComponent as ShareAltIcon } from "../../../../../images/share-alt.svg";
import { ReactComponent as PlusIcon } from "../../../../../images/plus-transparent.svg";
import { ReactComponent as CloseIcon } from "../../../../../images/cross.svg";

import AvatarPlaceholder from "../../../../../images/avatar-purple.svg";
import ContentPlaceholder from "../../../../../images/content-placeholder.svg";

import { getImageSource } from "../../../../../utils/methods";
import { TableContext } from "../../types";
import ChooseActionDropdownContainer from "src/app/Task/components/TaskMetaFields/components/ChooseActionDropdownContainer";
import useGetProjectTaskAutocompleteList from "src/app/Task/hooks/useGetProjectTaskAutocompleteList";

interface TaskTableFieldInterface {
  fieldType: MetaFieldType;
  fieldValue: any;
  onValueChange: (
    newValue: string | DateObject | null | number | boolean,
  ) => void;
  clearFieldValue: () => void;
  renderBasicTypes: () => ReactElement;
  shouldTriggerEditWarning?: boolean | undefined;
  tableContext?: TableContext;
  projectId: string;
}

export default function TaskTableField({
  fieldType,
  fieldValue,
  onValueChange,
  clearFieldValue,
  renderBasicTypes,
  shouldTriggerEditWarning,
  tableContext,
  projectId,
}: TaskTableFieldInterface) {
  const [action, setAction] = useState<any>({});

  const handleImageLoadError = (
    placeholder: string,
    e: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = placeholder;
  };

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const projectTasksAutocomplete = useGetProjectTaskAutocompleteList(
    projectId ?? null,
  );

  const history = useHistory();

  useEffect(() => {
    if (ACTION_TYPES.includes(fieldType) && projectTasksAutocomplete) {
      const actionCopy = projectTasksAutocomplete.find(
        (item) => item.taskId === fieldValue,
      );

      setAction(actionCopy);
    }
  }, [fieldValue, projectTasksAutocomplete, fieldType]);

  useEffect(() => {
    if (action && fieldValue !== action?.taskId) {
      onValueChange(action.taskId);
    }
    // TODO: fix useEffect dependencies https://app.asana.com/1/242052987772680/project/1159791777517936/task/1211753858596128?focus=true
    // prettier-ignore
    // eslint-disable-next-line
  }, [action]);

  const clearAction = () => {
    setAction(null);
    clearFieldValue();
  };

  const handleTaskOpen = (targetTaskId: string) => {
    if (tableContext === "workspaceTasksList") {
      const currentLocation =
        history.location.pathname + history.location.search;

      const foundTaskType = Object.values(TaskType).find(
        (task) => currentLocation.includes(task), // payments, creators, publications, etc.
      );

      history.push({
        pathname: `/workspace/${activeWorkspaceUuid}/summary/${projectId}/${targetTaskId}`,
        search: `taskType=${foundTaskType}`,
      });
    } else {
      history.push(
        `/workspace/${activeWorkspaceUuid}/projects/${projectId}/${targetTaskId}`,
      );
    }
  };

  const renderTaskField = () => {
    switch (fieldType) {
      case MetaFieldType.Creator:
      case MetaFieldType.Content:
      case MetaFieldType.PublicationRelated: {
        return action?.taskId ? (
          <span className="action-field-wrapper">
            <ChooseActionDropdownContainer
              projectId={projectId}
              fieldType={fieldType}
              action={action}
              setAction={setAction}
              toRight
            >
              <span
                className={classNames("action-field", {
                  "action-field--circle": fieldType === MetaFieldType.Creator,
                })}
              >
                {fieldType === MetaFieldType.Creator ? (
                  <img
                    src={getImageSource(
                      action?.cover,
                      "tiny",
                      AvatarPlaceholder,
                    )}
                    alt="avatar"
                    onError={(e) => handleImageLoadError(AvatarPlaceholder, e)}
                  />
                ) : fieldType === MetaFieldType.PublicationRelated ? (
                  <img
                    src={getImageSource(
                      action?.cover,
                      "tiny",
                      PublicationPlaceholder,
                    )}
                    alt={fieldType}
                    onError={(e) =>
                      handleImageLoadError(PublicationPlaceholder, e)
                    }
                  />
                ) : (
                  <img
                    src={getImageSource(
                      action?.cover,
                      "tiny",
                      ContentPlaceholder,
                    )}
                    alt={fieldType}
                    onError={(e) => handleImageLoadError(ContentPlaceholder, e)}
                  />
                )}
                <TooltipedEllipsis maxWidth={160} content={action.title} />
              </span>
            </ChooseActionDropdownContainer>
            <ShareAltIcon
              className="share-icon"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleTaskOpen(action.taskId);
              }}
            />
            {!shouldTriggerEditWarning && (
              <button
                type="button"
                onClick={clearAction}
                className="tasks-table__field-close"
              >
                <CloseIcon />
              </button>
            )}
          </span>
        ) : (
          <ChooseActionDropdownContainer
            projectId={projectId}
            fieldType={fieldType}
            action={action}
            setAction={setAction}
            toRight
          >
            <div className="tasks-table__empty-field">
              <PlusIcon />
            </div>
          </ChooseActionDropdownContainer>
        );
      }
      case MetaFieldType.Rating:
        return (
          <RatingSelector
            onValueChange={onValueChange}
            value={parseInt(fieldValue || "0", 10)}
          />
        );
      case MetaFieldType.Counter:
        return <div>{fieldValue}</div>;
      default:
        return renderBasicTypes();
    }
  };

  return renderTaskField();
}
